import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import client from "../lib/api";
import { mediaSrc } from "../lib/media";
import { useReasonOrder } from "../hooks/useReasonOrder";
import { X, Trophy, Ban, Undo2, MapPin, BedDouble, Bath, Flame, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const THRESHOLD = 110;

const SwipeCard = ({ p, priceLabel, onSwipe, top }) => {
  const [dx, setDx] = useState(0);
  const [drag, setDrag] = useState(false);
  const start = useRef(null);
  const fly = useRef(null); // "left" | "right" while animating out

  const onDown = (e) => { if (!top) return; start.current = e.clientX; setDrag(true); e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (!drag || start.current === null) return; setDx(e.clientX - start.current); };
  const finish = (dir) => { fly.current = dir; setDx(dir === "right" ? 900 : -900); setTimeout(() => onSwipe(dir, p), 220); };
  const onUp = () => {
    if (!drag) return;
    setDrag(false); start.current = null;
    if (dx > THRESHOLD) finish("right"); else if (dx < -THRESHOLD) finish("left"); else setDx(0);
  };
  useEffect(() => { if (!top) setDx(0); }, [top]);

  const rot = Math.max(-18, Math.min(18, dx / 12));
  const likeOp = Math.min(1, Math.max(0, dx / THRESHOLD));
  const nopeOp = Math.min(1, Math.max(0, -dx / THRESHOLD));
  return (
    <div data-testid={top ? "swipe-card-top" : undefined}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      style={{ transform: `translateX(${dx}px) rotate(${rot}deg) scale(${top ? 1 : 0.95}) translateY(${top ? 0 : 14}px)`, transition: drag ? "none" : "transform 220ms ease", touchAction: "pan-y", zIndex: top ? 2 : 1 }}
      className={`absolute inset-0 select-none overflow-hidden rounded-3xl border border-white/10 bg-card shadow-2xl ${top ? "cursor-grab active:cursor-grabbing" : "pointer-events-none opacity-80"}`}>
      <img src={mediaSrc(p.image)} alt="" draggable={false} className="h-[62%] w-full object-cover" />
      <div className="absolute inset-x-0 top-[40%] h-[22%] bg-gradient-to-t from-card to-transparent" />
      <span style={{ opacity: likeOp }} className="absolute left-5 top-5 rotate-[-12deg] rounded-xl border-4 border-brand-green px-3 py-1 font-display text-2xl font-black uppercase tracking-widest text-brand-green">{tr("Shortlist")}</span>
      <span style={{ opacity: nopeOp }} className="absolute right-5 top-5 rotate-[12deg] rounded-xl border-4 border-brand-red px-3 py-1 font-display text-2xl font-black uppercase tracking-widest text-brand-red">{tr("Nope")}</span>
      <div className="absolute inset-x-0 bottom-0 p-5">
        <p className="font-display text-xl font-bold leading-tight text-white">{p.title}</p>
        <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {p.district || p.city || "Marrakech"}</p>
        <div className="mt-3 flex items-center gap-4 text-sm text-slate-300">
          <span className="font-display text-lg font-semibold text-white">{priceLabel(p)}</span>
          <span className="flex items-center gap-1"><BedDouble className="h-4 w-4" />{p.beds}</span>
          <span className="flex items-center gap-1"><Bath className="h-4 w-4" />{p.baths}</span>
          {p.size ? <span>{p.size} m²</span> : null}
          <span className="ml-auto flex items-center gap-1 text-amber-300"><Flame className="h-4 w-4" />{p.score}</span>
        </div>
        {p.summary && <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{p.summary}</p>}
      </div>
    </div>
  );
};

export const SwipeDeck = ({ items, priceLabel, onChanged, onClose }) => {
  const [queue, setQueue] = useState(() => items.filter((p) => p.disposition !== "disqualified" && !p.rank));
  const [done, setDone] = useState({ shortlisted: 0, disqualified: 0 });
  const [pendingNope, setPendingNope] = useState(null); // property waiting for a reason
  const [last, setLast] = useState(null);                 // { p, prev } for undo
  const { reasons, frequent } = useReasonOrder();
  const takenRanks = useRef(new Set(items.filter((p) => p.rank > 0 && p.disposition !== "disqualified").map((p) => p.rank)));

  useEffect(() => {
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const pop = useCallback((p) => setQueue((q) => q.filter((x) => x.id !== p.id)), []);

  const shortlist = async (p) => {
    const free = [1, 2, 3, 4, 5].find((n) => !takenRanks.current.has(n));
    if (!free) { toast.error(tr("Top 5 is full"), { description: "Un-rank something first, or keep swiping to disqualify." }); return; }
    pop(p);
    try {
      const r = await client.post(`/properties/${p.id}/rank`, { rank: free });
      takenRanks.current.add(free); onChanged(r.data);
      setDone((d) => ({ ...d, shortlisted: d.shortlisted + 1 }));
      setLast({ p: r.data, prev: { rank: 0 } });
      toast.success(`${p.title} → Top 5 #${free}`, { action: { label: "Undo", onClick: () => undo({ p: r.data, prev: { rank: 0 } }) } });
    } catch (e) { toast.error(tr("Couldn't shortlist")); setQueue((q) => [p, ...q]); }
  };

  const disqualify = async (p, reason) => {
    setPendingNope(null); pop(p);
    try {
      const r = await client.put(`/properties/${p.id}`, { disposition: "disqualified", disqualifyReason: reason, disqualifyNote: "Swiped left" });
      onChanged(r.data);
      setDone((d) => ({ ...d, disqualified: d.disqualified + 1 }));
      setLast({ p: r.data, prev: { disposition: "active" } });
      toast(`${p.title} disqualified · ${reason}`, { action: { label: "Undo", onClick: () => undo({ p: r.data, prev: { disposition: "active" } }) } });
    } catch (e) { toast.error(tr("Couldn't disqualify")); setQueue((q) => [p, ...q]); }
  };

  const undo = async (entry) => {
    const l = entry || last;
    if (!l) return;
    try {
      let r;
      if ("rank" in l.prev) { r = await client.post(`/properties/${l.p.id}/rank`, { rank: 0 }); takenRanks.current.delete(l.p.rank); setDone((d) => ({ ...d, shortlisted: Math.max(0, d.shortlisted - 1) })); }
      else { r = await client.put(`/properties/${l.p.id}`, { disposition: "active" }); setDone((d) => ({ ...d, disqualified: Math.max(0, d.disqualified - 1) })); }
      onChanged(r.data); setQueue((q) => [r.data, ...q.filter((x) => x.id !== r.data.id)]); setLast(null);
      toast.success(tr("Undone"));
    } catch (e) { toast.error(tr("Couldn't undo")); }
  };

  const onSwipe = (dir, p) => { if (dir === "right") shortlist(p); else setPendingNope(p); };
  const top = queue[0];

  return createPortal(
    <div data-testid="swipe-deck" className="fixed inset-0 z-[70] flex flex-col bg-background">
      <header className="flex items-center justify-between px-4 py-3">
        <button onClick={onClose} data-testid="swipe-close" className="grid h-10 w-10 place-items-center rounded-full bg-white/[0.06] text-white"><X className="h-5 w-5" /></button>
        <div className="text-center">
          <p className="font-display text-base font-semibold text-white">{tr("Swipe to rank")}</p>
          <p className="text-[11px] text-muted-foreground" data-testid="swipe-progress">{queue.length} left · <span className="text-brand-green">{done.shortlisted} shortlisted</span> · <span className="text-brand-red">{done.disqualified} out</span></p>
        </div>
        <button onClick={() => undo()} disabled={!last} data-testid="swipe-undo" className="grid h-10 w-10 place-items-center rounded-full bg-white/[0.06] text-white disabled:opacity-30"><Undo2 className="h-5 w-5" /></button>
      </header>

      <div className="relative mx-auto w-full max-w-md flex-1 px-4 pb-2">
        {top ? (
          <div className="relative h-full min-h-[420px]">
            {queue.slice(0, 2).reverse().map((p) => <SwipeCard key={p.id} p={p} priceLabel={priceLabel} onSwipe={onSwipe} top={p.id === top.id} />)}
          </div>
        ) : (
          <div data-testid="swipe-empty" className="grid h-full place-items-center text-center">
            <div>
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-brand-green/15 text-brand-green"><Check className="h-7 w-7" /></span>
              <p className="mt-4 font-display text-xl font-semibold text-white">{tr("All caught up")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{done.shortlisted} shortlisted · {done.disqualified} disqualified. Everything else is already ranked.</p>
              <button onClick={onClose} className="mt-6 rounded-xl bg-brand-red px-6 py-3 font-semibold text-white">{tr("Back to properties")}</button>
            </div>
          </div>
        )}
      </div>

      {top && (
        <div className="mx-auto flex w-full max-w-md items-center justify-center gap-8 px-4 pb-8 pt-3">
          <button data-testid="swipe-nope-btn" onClick={() => setPendingNope(top)} className="grid h-16 w-16 place-items-center rounded-full border-2 border-brand-red/50 bg-brand-red/10 text-brand-red active:scale-95"><Ban className="h-7 w-7" /></button>
          <p className="text-center text-[11px] leading-tight text-muted-foreground"><ArrowLeft className="mx-auto h-3 w-3" />{tr("disqualify")}<br /><ArrowRight className="mx-auto mt-1 h-3 w-3" />{tr("shortlist")}</p>
          <button data-testid="swipe-like-btn" onClick={() => shortlist(top)} className="grid h-16 w-16 place-items-center rounded-full border-2 border-brand-green/50 bg-brand-green/10 text-brand-green active:scale-95"><Trophy className="h-7 w-7" /></button>
        </div>
      )}

      {pendingNope && (
        <div data-testid="swipe-reason-sheet" className="absolute inset-x-0 bottom-0 z-10 rounded-t-3xl border-t border-white/10 bg-[#0b0f13] p-5 pb-8 shadow-2xl">
          <p className="font-display text-base font-semibold text-white">Why is {pendingNope.title} out?</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {reasons.map((r) => (
              <button key={r} data-testid={`swipe-reason-${r.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => disqualify(pendingNope, r)}
                className={`relative rounded-xl border px-3 py-3 text-sm text-white active:bg-brand-red/20 ${frequent.includes(r) ? "border-brand-red/40 bg-brand-red/[0.08]" : "border-white/12 bg-white/[0.03]"}`}>
                {r}{frequent.includes(r) && <span data-testid="reason-frequent" className="absolute -top-1.5 right-2 rounded-full bg-brand-red px-1.5 text-[9px] font-bold uppercase text-white">{tr("often")}</span>}
              </button>
            ))}
          </div>
          <button onClick={() => setPendingNope(null)} data-testid="swipe-reason-cancel" className="mt-3 w-full py-2 text-sm text-muted-foreground">{tr("Keep it")}</button>
        </div>
      )}
    </div>,
    document.body,
  );
};
